import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  CalendarPlus,
  Clock,
  Languages,
  Pencil,
  Plus,
  RefreshCcw,
  Trash2,
  Youtube,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useParams } from 'react-router-dom';
import { z } from 'zod';
import { PageHeader } from '../components/AppShell';
import { BulkBar, toggleAll, toggleId } from '../components/BulkBar';
import { ImageUploadField } from '../components/ImageUploadField';
import { LiveBadge } from '../components/LiveBadge';
import { YouTubeEmbed } from '../components/YouTubeEmbed';
import { ExportMenu } from '../components/ExportMenu';
import { Topbar } from '../components/Topbar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Empty } from '../components/ui/empty';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Spinner } from '../components/ui/spinner';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { useConfirm } from '../components/ui/confirm';
import { useToast } from '../components/ui/toast';
import { useChannels } from '../lib/api/channels';
import {
  useCreateEpisode,
  useBulkDeleteEpisodes,
  useDeleteEpisode,
  useEpisodes,
  useUpdateEpisode,
} from '../lib/api/episodes';
import { exportEpisodes, exportProgrammeSlots } from '../lib/api/export';
import {
  useAddSlot,
  useDeleteProgrammeTranslation,
  useDeleteSlot,
  useBulkDeleteSlots,
  useProgramme,
  useUpdateProgramme,
  useUpdateSlot,
  useUpsertProgrammeTranslation,
} from '../lib/api/programmes';
import {
  useDetectProgrammeLive,
  useResolveYouTubeChannel,
  useSyncYouTube,
  useYouTubeLive,
} from '../lib/api/youtube';
import { dayLabel, fmtDateTime, fmtDuration } from '../lib/date';
import { useLocaleStore } from '../lib/locales';
import { slugify } from '../lib/slug';
import type {
  EpisodeInput,
  ProgrammeCategory,
  ProgrammeMedium,
  ProgrammeTranslationInput,
  PublicEpisode,
  PublicProgrammeSlot,
  SlotInput,
} from '../lib/types';

export default function ProgrammeDetail(): JSX.Element {
  const { slug } = useParams<{ slug: string }>();
  const locale = useLocaleStore((s) => s.current);
  const programme = useProgramme(slug ?? null, locale);

  return (
    <>
      <Topbar title={programme.data?.title ?? 'Programme'} subtitle={slug} />
      <div className="px-6 py-3">
        <Link
          to="/programmes"
          className="inline-flex items-center gap-1 text-sm text-ink-muted hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4" /> Back to programmes
        </Link>
      </div>
      {programme.isLoading ? (
        <div className="px-6 pt-6">
          <Spinner label="Loading…" />
        </div>
      ) : programme.data ? (
        <ProgrammeBody slug={slug ?? ''} programmeId={programme.data.id} />
      ) : (
        <div className="px-6 py-10">
          <Empty title="Programme not found" />
        </div>
      )}
    </>
  );
}

function ProgrammeBody({ slug, programmeId }: { slug: string; programmeId: string }): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const available = useLocaleStore((s) => s.available);
  const programme = useProgramme(slug, locale);
  const update = useUpdateProgramme();
  const { showToast } = useToast();

  return (
    <>
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            {programme.data?.title ?? slug}
            <LiveBadge programmeId={programmeId} />
          </span>
        }
        subtitle={`Default locale: ${(programme.data?.defaultLocale ?? '').toUpperCase()}`}
        actions={
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-ink-muted">Medium</Label>
              <Select
                value={programme.data?.medium ?? 'both'}
                onValueChange={async (v) => {
                  try {
                    await update.mutateAsync({
                      id: programmeId,
                      input: { medium: v as ProgrammeMedium },
                    });
                    showToast({
                      title: 'Medium updated',
                      description:
                        v === 'radio'
                          ? 'Surfaces on Listen tab only'
                          : v === 'tv'
                            ? 'Surfaces on Watch tab only'
                            : 'Surfaces on both Listen and Watch (Simulcast)',
                      variant: 'success',
                    });
                  } catch (err) {
                    showToast({
                      title: 'Failed',
                      description: (err as Error).message,
                      variant: 'danger',
                    });
                  }
                }}
              >
                <SelectTrigger className="h-8 w-32 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="radio">Radio</SelectItem>
                  <SelectItem value="tv">TV</SelectItem>
                  <SelectItem value="both">Simulcast</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-ink-muted">Category</Label>
              <Select
                value={programme.data?.category ?? 'other'}
                onValueChange={async (v) => {
                  try {
                    await update.mutateAsync({
                      id: programmeId,
                      input: { category: v as ProgrammeCategory },
                    });
                    showToast({
                      title: 'Category updated',
                      description: 'Player Programmes-tab filter chips will reflect the change.',
                      variant: 'success',
                    });
                  } catch (err) {
                    showToast({
                      title: 'Failed',
                      description: (err as Error).message,
                      variant: 'danger',
                    });
                  }
                }}
              >
                <SelectTrigger className="h-8 w-32 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="news">News</SelectItem>
                  <SelectItem value="talk">Talk</SelectItem>
                  <SelectItem value="music">Music</SelectItem>
                  <SelectItem value="faith">Faith</SelectItem>
                  <SelectItem value="cultural">Cultural</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="prog-active-toggle" className="text-xs text-ink-muted">
                Active
              </Label>
              <Switch
                id="prog-active-toggle"
                checked={programme.data?.isActive ?? false}
                onCheckedChange={async (v) => {
                  try {
                    await update.mutateAsync({ id: programmeId, input: { isActive: v } });
                    showToast({
                      title: v ? 'Programme activated' : 'Programme deactivated',
                      variant: 'success',
                    });
                  } catch (err) {
                    showToast({
                      title: 'Failed',
                      description: (err as Error).message,
                      variant: 'danger',
                    });
                  }
                }}
              />
            </div>
          </div>
        }
      />
      <div className="px-6 pb-10">
        <Tabs defaultValue="translations">
          <TabsList>
            <TabsTrigger value="translations">
              <Languages className="h-3.5 w-3.5" /> Translations
            </TabsTrigger>
            <TabsTrigger value="slots">
              <Clock className="h-3.5 w-3.5" /> Schedule slots
            </TabsTrigger>
            <TabsTrigger value="episodes">
              <CalendarPlus className="h-3.5 w-3.5" /> Episodes
            </TabsTrigger>
            <TabsTrigger value="youtube">
              <Youtube className="h-3.5 w-3.5" /> YouTube
            </TabsTrigger>
          </TabsList>

          <TabsContent value="translations">
            <TranslationsPanel
              programmeId={programmeId}
              slug={slug}
              availableLocales={available.map((l) => l.code)}
            />
          </TabsContent>
          <TabsContent value="slots">
            <SlotsPanel programmeId={programmeId} slug={slug} />
          </TabsContent>
          <TabsContent value="episodes">
            <EpisodesPanel programmeId={programmeId} />
          </TabsContent>
          <TabsContent value="youtube">
            <YouTubePanel programmeId={programmeId} slug={slug} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}

// ---------- Translations ----------

const translationSchema = z.object({
  title: z.string().min(1).max(512),
  summary: z.string().max(1024).optional().or(z.literal('')),
  description: z.string().max(20_000).optional().or(z.literal('')),
  imageUrl: z.string().url().or(z.literal('')).optional(),
});
type TranslationForm = z.input<typeof translationSchema>;

function TranslationsPanel({
  programmeId,
  slug,
  availableLocales,
}: {
  programmeId: string;
  slug: string;
  availableLocales: string[];
}): JSX.Element {
  const [activeLocale, setActiveLocale] = useState<string>(availableLocales[0] ?? 'en');
  const programme = useProgramme(slug, activeLocale);
  const upsert = useUpsertProgrammeTranslation();
  const del = useDeleteProgrammeTranslation();
  const { showToast } = useToast();

  const existing = programme.data?.translations?.find((t) => t.locale === activeLocale) ?? null;

  const translationValues = {
    title: existing?.title ?? '',
    summary: existing?.summary ?? '',
    description: existing?.description ?? '',
    imageUrl: existing?.imageUrl ?? '',
  };

  const form = useForm<TranslationForm>({
    resolver: zodResolver(translationSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: translationValues,
  });

  // Switching locale shows a different record, not a refetch of the
  // same one, so start clean: `keepDirtyValues` above would otherwise
  // carry text typed for one into the next.
  useEffect(() => {
    form.reset(translationValues, { keepDirtyValues: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLocale]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Per-locale content</CardTitle>
            <CardDescription>One title/summary/description per locale.</CardDescription>
          </div>
          <Select value={activeLocale} onValueChange={setActiveLocale}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableLocales.map((l) => (
                <SelectItem key={l} value={l}>
                  {l.toUpperCase()}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {programme.isLoading ? (
          <Spinner label="Loading…" />
        ) : (
          <form
            onSubmit={form.handleSubmit(async (values) => {
              const input: ProgrammeTranslationInput = {
                title: values.title,
                summary: values.summary?.trim() ? values.summary : null,
                description: values.description?.trim() ? values.description : null,
                imageUrl: values.imageUrl?.trim() ? values.imageUrl : null,
              };
              try {
                await upsert.mutateAsync({ id: programmeId, locale: activeLocale, input });
                showToast({ title: 'Translation saved', variant: 'success' });
              } catch (err) {
                showToast({
                  title: 'Save failed',
                  description: (err as Error).message,
                  variant: 'danger',
                });
              }
            })}
            className="grid gap-4"
          >
            <div>
              <Label>Title</Label>
              <Input {...form.register('title')} placeholder="Programme title" />
              {form.formState.errors.title ? (
                <p className="mt-1 text-xs text-danger">{form.formState.errors.title.message}</p>
              ) : null}
            </div>
            <div>
              <Label>Summary</Label>
              <Textarea
                rows={2}
                {...form.register('summary')}
                placeholder="One-liner used in lists"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea rows={6} {...form.register('description')} placeholder="Full description" />
            </div>
            <div>
              <Label>Cover image</Label>
              <ImageUploadField
                value={form.watch('imageUrl') ?? ''}
                onChange={(url) => form.setValue('imageUrl', url, { shouldDirty: true })}
                category="programme-cover"
                maxDimension={1024}
                hint="Square 1024×1024 or larger looks best on the programme detail screen."
              />
              {form.formState.errors.imageUrl ? (
                <p className="mt-1 text-xs text-danger">{form.formState.errors.imageUrl.message}</p>
              ) : null}
            </div>
            <div className="flex items-center justify-between border-t border-line pt-3">
              <div>
                {existing ? (
                  <Button
                    type="button"
                    variant="ghost"
                    className="text-danger hover:bg-danger/10"
                    onClick={async () => {
                      try {
                        await del.mutateAsync({ id: programmeId, locale: activeLocale });
                        showToast({ title: 'Translation removed', variant: 'success' });
                        form.reset(
                          { title: '', summary: '', description: '', imageUrl: '' },
                          // The translation is gone: clear every field, including
                          // ones the user had edited.
                          { keepDirtyValues: false },
                        );
                      } catch (err) {
                        showToast({
                          title: 'Delete failed',
                          description: (err as Error).message,
                          variant: 'danger',
                        });
                      }
                    }}
                  >
                    <Trash2 className="h-4 w-4" /> Remove this translation
                  </Button>
                ) : null}
              </div>
              <Button type="submit" disabled={upsert.isPending}>
                {upsert.isPending ? 'Saving…' : existing ? 'Save changes' : 'Add translation'}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

// ---------- Slots ----------

const slotSchema = z.object({
  channelSlug: z.string().min(1),
  locale: z.string().regex(/^[a-z]{2,8}$/),
  /** One row per selected day will be created — programmes that air every
   *  weekday at 06:00 used to need five separate dialog passes; now it's one
   *  click per day before submitting. The fan-out happens client-side
   *  (`Promise.all` on `addSlot.mutateAsync`) so the schedule read-path,
   *  which already keys on a single `dayOfWeek`, is unchanged. */
  daysOfWeek: z.array(z.number().int().min(0).max(6)).min(1, 'Pick at least one day'),
  startTime: z.string().regex(/^\d{1,2}:\d{2}(?::\d{2})?$/),
  durationMinutes: z.coerce.number().int().positive().max(1440),
  isActive: z.boolean().default(true),
});
type SlotForm = z.input<typeof slotSchema>;

function SlotsPanel({ programmeId, slug }: { programmeId: string; slug: string }): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const available = useLocaleStore((s) => s.available);
  const programme = useProgramme(slug, locale);
  const channels = useChannels();
  const addSlot = useAddSlot();
  const updateSlot = useUpdateSlot();
  const delSlot = useDeleteSlot();
  const bulkDeleteSlots = useBulkDeleteSlots();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<PublicProgrammeSlot | null>(null);
  const [selectedSlots, setSelectedSlots] = useState<Set<string>>(new Set());
  const { showToast } = useToast();
  const { confirm, confirmDialog } = useConfirm();

  const editSchema = slotSchema.omit({ daysOfWeek: true }).extend({
    dayOfWeek: z.number().int().min(0).max(6),
  });
  type EditForm = z.input<typeof editSchema>;

  const editValues: EditForm = editing
    ? {
        channelSlug: editing.channelSlug,
        locale: editing.locale,
        dayOfWeek: editing.dayOfWeek,
        startTime: editing.startTime,
        durationMinutes: editing.durationMinutes,
        isActive: editing.isActive,
      }
    : { channelSlug: '', locale, dayOfWeek: 0, startTime: '06:00', durationMinutes: 60, isActive: true };

  const editForm = useForm<EditForm>({
    resolver: zodResolver(editSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: editValues,
  });

  // Switching slot shows a different record, not a refetch of the
  // same one, so start clean: `keepDirtyValues` above would otherwise
  // carry text typed for one into the next.
  useEffect(() => {
    editForm.reset(editValues, { keepDirtyValues: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  const form = useForm<SlotForm>({
    resolver: zodResolver(slotSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: {
      channelSlug: channels.data?.items[0]?.slug ?? '',
      locale,
      daysOfWeek: [],
      startTime: '06:00',
      durationMinutes: 60,
      isActive: true,
    },
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Schedule slots</CardTitle>
            <CardDescription>
              Local Lagos time. Multiple slots per programme per day are allowed; per-locale.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <ExportMenu
              size="sm"
              options={[
                {
                  format: 'csv',
                  label: 'Slots CSV',
                  description: 'One row per slot incl. inactive ones',
                  run: () => exportProgrammeSlots(slug, locale),
                },
              ]}
            />
            <Button onClick={() => setOpen(true)} disabled={!channels.data?.items.length}>
              <Plus className="h-4 w-4" /> Add slot
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {!Array.isArray(programme.data?.slots) || programme.data.slots.length === 0 ? (
          <Empty
            title="No slots scheduled"
            description="Add a recurring slot on a channel for this programme."
          />
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    checked={(programme.data?.slots?.length ?? 0) > 0 && (programme.data?.slots ?? []).every((s) => selectedSlots.has(s.id))}
                    onChange={() => setSelectedSlots(toggleAll(selectedSlots, (programme.data?.slots ?? []).map((s) => s.id)))}
                    aria-label="Select all slots"
                  />
                </th>
                <th className="w-32">Channel</th>
                <th className="w-24">Locale</th>
                <th className="w-24">Day</th>
                <th className="w-28">Start</th>
                <th className="w-28">Duration</th>
                <th className="w-24">Active</th>
                <th className="w-20 text-right" />
              </tr>
            </thead>
            <tbody>
              {programme.data?.slots?.map((s) => (
                <tr key={s.id} className={selectedSlots.has(s.id) ? 'bg-brand/5' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={selectedSlots.has(s.id)}
                      onChange={() => setSelectedSlots(toggleId(selectedSlots, s.id))}
                      aria-label={`Select slot ${s.id}`}
                    />
                  </td>
                  <td className="font-medium text-ink">{s.channelName}</td>
                  <td>
                    <Badge variant="outline" className="uppercase">
                      {s.locale}
                    </Badge>
                  </td>
                  <td>{dayLabel(s.dayOfWeek)}</td>
                  <td className="font-mono">{s.startTime}</td>
                  <td>{fmtDuration(s.durationMinutes * 60)}</td>
                  <td>
                    <Badge variant={s.isActive ? 'success' : 'neutral'}>
                      {s.isActive ? 'On' : 'Off'}
                    </Badge>
                  </td>
                  <td className="whitespace-nowrap text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditing(s)}
                      aria-label="Edit slot"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={async () => {
                        try {
                          await delSlot.mutateAsync({ programmeId, slotId: s.id });
                          showToast({ title: 'Slot removed', variant: 'success' });
                        } catch (err) {
                          showToast({
                            title: 'Delete failed',
                            description: (err as Error).message,
                            variant: 'danger',
                          });
                        }
                      }}
                    >
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      <Dialog open={open} onOpenChange={(o) => !o && setOpen(false)}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Add slot(s)</DialogTitle>
            <DialogDescription>
              Local Africa/Lagos time. Selecting multiple days creates one slot per day in one go.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={form.handleSubmit(async (values) => {
              const parsed = slotSchema.parse(values);
              try {
                // One row per selected day. Fired in parallel; toast surfaces
                // the count plus any per-day failures.
                const results = await Promise.allSettled(
                  parsed.daysOfWeek.map((dayOfWeek) => {
                    const input: SlotInput = {
                      channelSlug: parsed.channelSlug,
                      locale: parsed.locale,
                      dayOfWeek,
                      startTime: parsed.startTime,
                      durationMinutes: parsed.durationMinutes,
                      isActive: parsed.isActive,
                    };
                    return addSlot.mutateAsync({ programmeId, input });
                  }),
                );
                const ok = results.filter((r) => r.status === 'fulfilled').length;
                const failed = results.length - ok;
                if (failed === 0) {
                  showToast({
                    title: ok === 1 ? 'Slot added' : `${ok} slots added`,
                    variant: 'success',
                  });
                  setOpen(false);
                } else {
                  const firstError = results.find(
                    (r): r is PromiseRejectedResult => r.status === 'rejected',
                  );
                  showToast({
                    title: `${ok} added, ${failed} failed`,
                    description: (firstError?.reason as Error | undefined)?.message,
                    variant: 'danger',
                  });
                }
              } catch (err) {
                showToast({
                  title: 'Add failed',
                  description: (err as Error).message,
                  variant: 'danger',
                });
              }
            })}
            className="grid gap-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Channel</Label>
                <Select
                  value={form.watch('channelSlug')}
                  onValueChange={(v) => form.setValue('channelSlug', v, { shouldDirty: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.data?.items.map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Locale</Label>
                <Select
                  value={form.watch('locale')}
                  onValueChange={(v) => form.setValue('locale', v, { shouldDirty: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {available.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.code.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Days</Label>
                <div className="flex gap-2 text-xs">
                  <button
                    type="button"
                    className="text-ink-subtle hover:text-brand"
                    onClick={() => form.setValue('daysOfWeek', [0, 1, 2, 3, 4], { shouldValidate: true, shouldDirty: true })}
                  >
                    Weekdays
                  </button>
                  <span className="text-line">·</span>
                  <button
                    type="button"
                    className="text-ink-subtle hover:text-brand"
                    onClick={() => form.setValue('daysOfWeek', [5, 6], { shouldValidate: true, shouldDirty: true })}
                  >
                    Weekend
                  </button>
                  <span className="text-line">·</span>
                  <button
                    type="button"
                    className="text-ink-subtle hover:text-brand"
                    onClick={() => form.setValue('daysOfWeek', [0, 1, 2, 3, 4, 5, 6], { shouldValidate: true, shouldDirty: true })}
                  >
                    Every day
                  </button>
                </div>
              </div>
              <div className="mt-1 flex gap-1">
                {Array.from({ length: 7 }, (_, i) => i).map((i) => {
                  const selected = (form.watch('daysOfWeek') ?? []).includes(i);
                  return (
                    <button
                      key={`day-${i}`}
                      type="button"
                      onClick={() => {
                        const cur = form.watch('daysOfWeek') ?? [];
                        const next = cur.includes(i)
                          ? cur.filter((d) => d !== i)
                          : [...cur, i].sort((a, b) => a - b);
                        form.setValue('daysOfWeek', next, { shouldValidate: true, shouldDirty: true });
                      }}
                      className={
                        'flex-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ' +
                        (selected
                          ? 'border-brand bg-brand text-brand-foreground'
                          : 'border-line bg-surface text-ink hover:bg-surface-muted')
                      }
                      aria-pressed={selected}
                    >
                      {dayLabel(i)}
                    </button>
                  );
                })}
              </div>
              {form.formState.errors.daysOfWeek ? (
                <p className="mt-1 text-xs text-danger">
                  {form.formState.errors.daysOfWeek.message}
                </p>
              ) : null}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start time</Label>
                <Input type="time" step="60" {...form.register('startTime')} />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" min={1} max={1440} {...form.register('durationMinutes')} />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border border-line bg-surface-muted px-3 py-2">
              <Label className="cursor-pointer">Active</Label>
              <Switch
                checked={form.watch('isActive')}
                onCheckedChange={(v) => form.setValue('isActive', v, { shouldDirty: true })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={addSlot.isPending}>
                {addSlot.isPending
                  ? 'Adding…'
                  : (form.watch('daysOfWeek')?.length ?? 0) > 1
                    ? `Add ${form.watch('daysOfWeek')?.length} slots`
                    : 'Add slot'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit slot dialog */}
      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>Edit slot</DialogTitle>
            <DialogDescription>Local Africa/Lagos time.</DialogDescription>
          </DialogHeader>
          <form
            onSubmit={editForm.handleSubmit(async (values) => {
              if (!editing) return;
              try {
                await updateSlot.mutateAsync({
                  programmeId,
                  slotId: editing.id,
                  input: {
                    channelSlug: values.channelSlug,
                    locale: values.locale,
                    dayOfWeek: values.dayOfWeek,
                    startTime: values.startTime,
                    durationMinutes: values.durationMinutes,
                    isActive: values.isActive,
                  },
                });
                showToast({ title: 'Slot updated', variant: 'success' });
                setEditing(null);
              } catch (err) {
                showToast({
                  title: 'Update failed',
                  description: (err as Error).message,
                  variant: 'danger',
                });
              }
            })}
            className="grid gap-4"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Channel</Label>
                <Select
                  value={editForm.watch('channelSlug')}
                  onValueChange={(v) => editForm.setValue('channelSlug', v, { shouldDirty: true })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {channels.data?.items.map((c) => (
                      <SelectItem key={c.slug} value={c.slug}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Locale</Label>
                <Select
                  value={editForm.watch('locale')}
                  onValueChange={(v) => editForm.setValue('locale', v, { shouldDirty: true })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {available.map((l) => (
                      <SelectItem key={l.code} value={l.code}>{l.code.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label>Day</Label>
              <div className="mt-1 flex gap-1">
                {Array.from({ length: 7 }, (_, i) => i).map((i) => {
                  const selected = editForm.watch('dayOfWeek') === i;
                  return (
                    <button
                      key={`edit-day-${i}`}
                      type="button"
                      onClick={() => editForm.setValue('dayOfWeek', i, { shouldDirty: true })}
                      className={
                        'flex-1 rounded-md border px-2 py-1.5 text-xs font-medium transition-colors ' +
                        (selected
                          ? 'border-brand bg-brand text-brand-foreground'
                          : 'border-line bg-surface text-ink hover:bg-surface-muted')
                      }
                      aria-pressed={selected}
                    >
                      {dayLabel(i)}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Start time</Label>
                <Input type="time" step="60" {...editForm.register('startTime')} />
              </div>
              <div>
                <Label>Duration (min)</Label>
                <Input type="number" min={1} max={1440} {...editForm.register('durationMinutes')} />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border border-line bg-surface-muted px-3 py-2">
              <Label className="cursor-pointer">Active</Label>
              <Switch
                checked={editForm.watch('isActive')}
                onCheckedChange={(v) => editForm.setValue('isActive', v, { shouldDirty: true })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
              <Button type="submit" disabled={updateSlot.isPending}>
                {updateSlot.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <BulkBar
        selected={selectedSlots}
        onClear={() => setSelectedSlots(new Set())}
        entityLabel="slot"
        deleting={bulkDeleteSlots.isPending}
        onDelete={async () => {
          if (
            !(await confirm({
              title: `Delete ${selectedSlots.size} slot(s)?`,
              description: 'Removes the selected schedule slots. This cannot be undone.',
              confirmLabel: 'Delete',
            }))
          )
            return;
          try {
            const result = await bulkDeleteSlots.mutateAsync({ programmeId, ids: [...selectedSlots] });
            showToast({ title: `${result.deleted} slot(s) deleted`, variant: 'success' });
            setSelectedSlots(new Set());
          } catch (err) {
            showToast({ title: 'Delete failed', description: (err as Error).message, variant: 'danger' });
          }
        }}
      />
      {confirmDialog}
    </Card>
  );
}

// ---------- Episodes ----------

const episodeSchema = z.object({
  slug: z
    .string()
    .min(1)
    .regex(/^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i),
  locale: z.string().regex(/^[a-z]{2,8}$/),
  title: z.string().min(1).max(512),
  description: z.string().optional().or(z.literal('')),
  airDateGmt: z.string().min(1),
  durationSeconds: z.coerce.number().int().nonnegative().optional(),
  audioUrl: z.string().url().or(z.literal('')).optional(),
  videoUrl: z.string().url().or(z.literal('')).optional(),
  youtubeVideoId: z
    .string()
    .regex(/^[A-Za-z0-9_-]{6,32}$/, 'expected YouTube video id')
    .or(z.literal(''))
    .optional(),
  imageUrl: z.string().url().or(z.literal('')).optional(),
  isPublished: z.boolean().default(true),
});
type EpisodeForm = z.input<typeof episodeSchema>;

function EpisodesPanel({ programmeId }: { programmeId: string }): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const available = useLocaleStore((s) => s.available);
  const list = useEpisodes({ programmeId, locale, page: 1, pageSize: 50 });
  const create = useCreateEpisode();
  const update = useUpdateEpisode();
  const del = useDeleteEpisode();
  const bulkDeleteEpisodes = useBulkDeleteEpisodes();
  const [editing, setEditing] = useState<PublicEpisode | 'new' | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState<PublicEpisode | null>(null);
  const [selectedEpisodes, setSelectedEpisodes] = useState<Set<string>>(new Set());
  const { showToast } = useToast();
  const { confirm, confirmDialog } = useConfirm();

  // Fixed at mount — see the note in Events.tsx: a per-render `new Date()`
  // makes `values` churn every minute and reverts untouched fields.
  const [nowLocalInput] = useState(() => new Date().toISOString().slice(0, 16));

  const episodeValues: EpisodeForm =
      editing && editing !== 'new'
        ? {
            slug: editing.slug,
            locale: editing.locale,
            title: editing.title,
            description: editing.description ?? '',
            airDateGmt: editing.airDateGmt.slice(0, 16),
            durationSeconds: editing.durationSeconds ?? undefined,
            audioUrl: editing.audioUrl ?? '',
            videoUrl: editing.videoUrl ?? '',
            youtubeVideoId: editing.youtubeVideoId ?? '',
            imageUrl: editing.imageUrl ?? '',
            isPublished: editing.publishedAt !== null,
          }
        : {
            slug: '',
            locale,
            title: '',
            description: '',
            airDateGmt: nowLocalInput,
            durationSeconds: 0,
            audioUrl: '',
            videoUrl: '',
            youtubeVideoId: '',
            imageUrl: '',
            isPublished: true,
          };

  const form = useForm<EpisodeForm>({
    resolver: zodResolver(episodeSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: episodeValues,
  });

  // Switching episode shows a different record, not a refetch of the
  // same one, so start clean: `keepDirtyValues` above would otherwise
  // carry text typed for one into the next.
  useEffect(() => {
    form.reset(episodeValues, { keepDirtyValues: false });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);

  // Auto-derive slug from title only when creating a new episode and the user
  // hasn't manually edited the slug field. Existing episodes show their stored
  // slug verbatim and aren't re-derived from title edits.
  const isCreating = editing === 'new';
  const watchedTitle = form.watch('title');
  const slugTouched = form.formState.dirtyFields.slug;
  useEffect(() => {
    if (!isCreating || slugTouched) return;
    const candidate = slugify(watchedTitle ?? '');
    if (candidate) form.setValue('slug', candidate, { shouldDirty: false });
  }, [watchedTitle, isCreating, slugTouched, form]);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle>Episodes ({locale.toUpperCase()})</CardTitle>
            <CardDescription>
              Per-locale episodes. Use the locale switcher in the topbar to change scope, or
              override per-episode below.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <ExportMenu
              size="sm"
              options={[
                {
                  format: 'csv',
                  label: 'Episodes CSV',
                  description: `Episodes in ${locale.toUpperCase()} for this programme`,
                  run: () => exportEpisodes({ format: 'csv', programmeId, locale }),
                },
                {
                  format: 'json',
                  label: 'Episodes JSON',
                  run: () => exportEpisodes({ format: 'json', programmeId, locale }),
                },
              ]}
            />
            <Button onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" /> New episode
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {list.isLoading ? (
          <Spinner label="Loading…" />
        ) : Array.isArray(list.data?.items) && list.data.items.length > 0 ? (
          <table className="table-base">
            <thead>
              <tr>
                <th className="w-8">
                  <input
                    type="checkbox"
                    className="cursor-pointer"
                    checked={(list.data?.items?.length ?? 0) > 0 && (list.data?.items ?? []).every((ep) => selectedEpisodes.has(ep.id))}
                    onChange={() => setSelectedEpisodes(toggleAll(selectedEpisodes, (list.data?.items ?? []).map((ep) => ep.id)))}
                    aria-label="Select all episodes"
                  />
                </th>
                <th>Title</th>
                <th className="w-24">Locale</th>
                <th className="w-44">Air date (GMT)</th>
                <th className="w-32">Duration</th>
                <th className="w-28">Status</th>
                <th className="w-24 text-right" />
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((ep) => (
                <tr key={ep.id} className={selectedEpisodes.has(ep.id) ? 'bg-brand/5' : ''}>
                  <td>
                    <input
                      type="checkbox"
                      className="cursor-pointer"
                      checked={selectedEpisodes.has(ep.id)}
                      onChange={() => setSelectedEpisodes(toggleId(selectedEpisodes, ep.id))}
                      aria-label={`Select ${ep.slug}`}
                    />
                  </td>
                  <td className="font-medium text-ink">{ep.title}</td>
                  <td>
                    <Badge variant="outline" className="uppercase">
                      {ep.locale}
                    </Badge>
                  </td>
                  <td className="text-sm text-ink-muted">{fmtDateTime(ep.airDateGmt)}</td>
                  <td>{fmtDuration(ep.durationSeconds)}</td>
                  <td>
                    <Badge variant={ep.publishedAt ? 'success' : 'warning'}>
                      {ep.publishedAt ? 'Published' : 'Draft'}
                    </Badge>
                  </td>
                  <td className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setEditing(ep)}
                      aria-label="Edit"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setConfirmingDelete(ep)}
                      aria-label="Delete"
                    >
                      <Trash2 className="h-4 w-4 text-danger" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <Empty
            title={`No episodes in ${locale.toUpperCase()}`}
            description="Add an episode for this locale."
          />
        )}
      </CardContent>

      <Dialog
        open={confirmingDelete !== null}
        onOpenChange={(o) => {
          if (!o) setConfirmingDelete(null);
        }}
      >
        <DialogContent size="sm">
          <DialogHeader>
            <DialogTitle>Delete episode?</DialogTitle>
            <DialogDescription>
              {confirmingDelete ? (
                <>
                  This will permanently remove{' '}
                  <span className="font-medium text-ink">"{confirmingDelete.title}"</span> from
                  the catalog. The action can't be undone — re-syncing from YouTube will only
                  bring back episodes that match an unimported videoId.
                </>
              ) : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmingDelete(null)}
              disabled={del.isPending}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              className="bg-danger hover:bg-danger/90"
              disabled={del.isPending}
              onClick={async () => {
                if (!confirmingDelete) return;
                try {
                  await del.mutateAsync({ programmeId, episodeId: confirmingDelete.id });
                  showToast({ title: 'Episode deleted', variant: 'success' });
                  setConfirmingDelete(null);
                } catch (err) {
                  showToast({
                    title: 'Delete failed',
                    description: (err as Error).message,
                    variant: 'danger',
                  });
                }
              }}
            >
              {del.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>
              {editing && editing !== 'new' ? 'Edit episode' : 'New episode'}
            </DialogTitle>
          </DialogHeader>
          <form
            onSubmit={form.handleSubmit(async (values) => {
              const input: EpisodeInput = {
                slug: values.slug,
                locale: values.locale,
                title: values.title,
                description: values.description ? values.description : null,
                airDateGmt: new Date(values.airDateGmt).toISOString(),
                durationSeconds: values.durationSeconds ?? null,
                audioUrl: values.audioUrl ? values.audioUrl : null,
                videoUrl: values.videoUrl ? values.videoUrl : null,
                youtubeVideoId: values.youtubeVideoId ? values.youtubeVideoId : null,
                imageUrl: values.imageUrl ? values.imageUrl : null,
                isPublished: values.isPublished,
              };
              try {
                if (editing && editing !== 'new') {
                  await update.mutateAsync({ programmeId, episodeId: editing.id, input });
                  showToast({ title: 'Episode updated', variant: 'success' });
                } else {
                  await create.mutateAsync({ programmeId, input });
                  showToast({ title: 'Episode created', variant: 'success' });
                }
                // Mutation onSuccess invalidates the episodes query — the list re-renders without a reload.
                setEditing(null);
              } catch (err) {
                showToast({
                  title: 'Save failed',
                  description: (err as Error).message,
                  variant: 'danger',
                });
              }
            })}
            className="grid gap-4"
          >
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <Label>Title</Label>
                <Input {...form.register('title')} autoFocus />
                {form.formState.errors.title ? (
                  <p className="mt-1 text-xs text-danger">{form.formState.errors.title.message}</p>
                ) : null}
              </div>
              <div>
                <Label>Locale</Label>
                <Select
                  value={form.watch('locale')}
                  onValueChange={(v) => form.setValue('locale', v, { shouldDirty: true })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {available.map((l) => (
                      <SelectItem key={l.code} value={l.code}>
                        {l.code.toUpperCase()}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Slug</Label>
                <Input
                  {...form.register('slug')}
                  placeholder={isCreating ? 'auto-from-title' : 'episode-slug'}
                />
                {isCreating && !slugTouched ? (
                  <p className="mt-1 text-xs text-ink-subtle">Auto-generated. Edit to customise.</p>
                ) : null}
                {form.formState.errors.slug ? (
                  <p className="mt-1 text-xs text-danger">{form.formState.errors.slug.message}</p>
                ) : null}
              </div>
              <div>
                <Label>Air date (UTC)</Label>
                <Input type="datetime-local" {...form.register('airDateGmt')} />
              </div>
            </div>

            <div>
              <Label>Description</Label>
              <Textarea rows={3} {...form.register('description')} />
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Duration (sec)</Label>
                <Input type="number" min={0} {...form.register('durationSeconds')} />
              </div>
              <div>
                <Label>Audio URL</Label>
                <Input {...form.register('audioUrl')} placeholder="https://…/episode.mp3" />
              </div>
              <div>
                <Label>Video URL</Label>
                <Input {...form.register('videoUrl')} placeholder="https://youtube.com/…" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>YouTube video ID</Label>
                <Input
                  {...form.register('youtubeVideoId')}
                  placeholder="dQw4w9WgXcQ"
                />
                {form.formState.errors.youtubeVideoId ? (
                  <p className="mt-1 text-xs text-danger">
                    {form.formState.errors.youtubeVideoId.message}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-ink-subtle">
                    Powers the embed player and YouTube sync dedupe.
                  </p>
                )}
              </div>
              <div>
                <Label>Episode image</Label>
                <ImageUploadField
                  value={form.watch('imageUrl') ?? ''}
                  onChange={(url) => form.setValue('imageUrl', url, { shouldDirty: true })}
                  category="episode-image"
                  maxDimension={1280}
                  hint="Wide 16:9 thumbnail works best on the episode list."
                />
              </div>
            </div>

            <div className="flex items-center justify-between rounded-md border border-line bg-surface-muted px-3 py-2">
              <Label className="cursor-pointer">Published</Label>
              <Switch
                checked={form.watch('isPublished')}
                onCheckedChange={(v) => form.setValue('isPublished', v, { shouldDirty: true })}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>
                Cancel
              </Button>
              <Button type="submit" disabled={create.isPending || update.isPending}>
                {create.isPending || update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <BulkBar
        selected={selectedEpisodes}
        onClear={() => setSelectedEpisodes(new Set())}
        entityLabel="episode"
        deleting={bulkDeleteEpisodes.isPending}
        onDelete={async () => {
          if (
            !(await confirm({
              title: `Delete ${selectedEpisodes.size} episode(s)?`,
              description: 'Removes the selected episodes. This cannot be undone.',
              confirmLabel: 'Delete',
            }))
          )
            return;
          try {
            const result = await bulkDeleteEpisodes.mutateAsync({ programmeId, ids: [...selectedEpisodes] });
            showToast({ title: `${result.deleted} episode(s) deleted`, variant: 'success' });
            setSelectedEpisodes(new Set());
          } catch (err) {
            showToast({ title: 'Delete failed', description: (err as Error).message, variant: 'danger' });
          }
        }}
      />
      {confirmDialog}
    </Card>
  );
}

// ---------- YouTube ----------

const youtubeSchema = z.object({
  youtubeChannelId: z
    .string()
    .regex(/^UC[A-Za-z0-9_-]{20,30}$/, 'expected UC… channel id')
    .or(z.literal(''))
    .optional(),
  youtubeHandle: z
    .string()
    .regex(/^@?[A-Za-z0-9._-]{3,32}$/, 'expected @handle')
    .or(z.literal(''))
    .optional(),
  youtubeLiveStreamId: z
    .string()
    .regex(/^[A-Za-z0-9_-]{6,32}$/, 'expected video id')
    .or(z.literal(''))
    .optional(),
});
type YouTubeForm = z.input<typeof youtubeSchema>;

function YouTubePanel({ programmeId, slug }: { programmeId: string; slug: string }): JSX.Element {
  const locale = useLocaleStore((s) => s.current);
  const programme = useProgramme(slug, locale);
  const update = useUpdateProgramme();
  const resolve = useResolveYouTubeChannel();
  const detectLive = useDetectProgrammeLive();
  const sync = useSyncYouTube();
  const live = useYouTubeLive(programmeId);
  const { showToast } = useToast();

  const form = useForm<YouTubeForm>({
    resolver: zodResolver(youtubeSchema),
    // Keeps what the user has typed when this form re-syncs from the
    // server — a refetch or another admin's edit updates only the
    // fields they haven't touched, instead of wiping the whole form.
    resetOptions: { keepDirtyValues: true },
    values: {
      youtubeChannelId: programme.data?.youtubeChannelId ?? '',
      youtubeHandle: programme.data?.youtubeHandle ?? '',
      youtubeLiveStreamId: programme.data?.youtubeLiveStreamId ?? '',
    },
  });

  return (
    <div className="grid gap-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>YouTube source</CardTitle>
              <CardDescription>
                Configure the channel powering this programme's live status and episode sync.
              </CardDescription>
            </div>
            <LiveBadge programmeId={programmeId} />
          </div>
        </CardHeader>
        <CardContent>
          <form
            onSubmit={form.handleSubmit(async (values) => {
              try {
                await update.mutateAsync({
                  id: programmeId,
                  input: {
                    youtubeChannelId: values.youtubeChannelId ? values.youtubeChannelId : null,
                    youtubeHandle: values.youtubeHandle ? values.youtubeHandle : null,
                    youtubeLiveStreamId: values.youtubeLiveStreamId
                      ? values.youtubeLiveStreamId
                      : null,
                  },
                });
                showToast({ title: 'YouTube settings saved', variant: 'success' });
              } catch (err) {
                showToast({
                  title: 'Save failed',
                  description: (err as Error).message,
                  variant: 'danger',
                });
              }
            })}
            className="grid gap-3"
          >
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Channel ID</Label>
                <Input
                  {...form.register('youtubeChannelId')}
                  placeholder="UCxxxxxxxxxxxxxxxxxxxxxx"
                />
                {form.formState.errors.youtubeChannelId ? (
                  <p className="mt-1 text-xs text-danger">
                    {form.formState.errors.youtubeChannelId.message}
                  </p>
                ) : (
                  <p className="mt-1 text-xs text-ink-subtle">
                    Canonical UC… id. Auto-resolved from handle if blank.
                  </p>
                )}
              </div>
              <div>
                <Label>Handle</Label>
                <Input {...form.register('youtubeHandle')} placeholder="@VONOfficial" />
                {form.formState.errors.youtubeHandle ? (
                  <p className="mt-1 text-xs text-danger">
                    {form.formState.errors.youtubeHandle.message}
                  </p>
                ) : null}
              </div>
            </div>
            <div>
              <Label>Pinned live stream ID (optional) e.g. d4zDorDl5UE</Label>
              <Input
                {...form.register('youtubeLiveStreamId')}
                placeholder="A specific upcoming or live video id"
              />
              <p className="mt-1 text-xs text-ink-subtle">
                Set this when you want the live badge to follow a specific broadcast — saves
                quota by skipping channel-search discovery.
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 pt-1">
              {/* Detect live: one YouTube search.list (100 quota units)
               *  that finds the currently-airing broadcast on this
               *  programme's YouTube channel and auto-pins its video id.
               *  Same identity requirement as Resolve — needs the row to
               *  already have channelId or handle stored. Disabled while
               *  either query is inflight to prevent double-clicks. */}
              <Button
                type="button"
                variant="outline"
                disabled={
                  detectLive.isPending ||
                  (!programme.data?.youtubeHandle && !programme.data?.youtubeChannelId)
                }
                onClick={async () => {
                  try {
                    const r = await detectLive.mutateAsync(programmeId);
                    if (r.detected) {
                      form.setValue('youtubeLiveStreamId', r.videoId, { shouldDirty: true });
                      showToast({
                        title: 'Live stream detected',
                        description: r.title ? `Pinned: ${r.title}` : `Pinned video id ${r.videoId}`,
                        variant: 'success',
                      });
                    } else {
                      showToast({
                        title: 'Nothing currently live',
                        description:
                          'This YouTube channel has no live broadcast right now. Try again when they go live.',
                        variant: 'info',
                      });
                    }
                  } catch (err) {
                    showToast({
                      title: 'Detect failed',
                      description: (err as Error).message,
                      variant: 'danger',
                    });
                  }
                }}
              >
                {detectLive.isPending ? 'Detecting…' : 'Detect live'}
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={resolve.isPending || (!programme.data?.youtubeHandle && !programme.data?.youtubeChannelId)}
                onClick={async () => {
                  try {
                    const r = await resolve.mutateAsync(programmeId);
                    form.setValue('youtubeChannelId', r.channelId, { shouldDirty: true });
                    showToast({
                      title: 'Resolved',
                      description: r.title ?? r.channelId,
                      variant: 'success',
                    });
                  } catch (err) {
                    showToast({
                      title: 'Resolve failed',
                      description: (err as Error).message,
                      variant: 'danger',
                    });
                  }
                }}
              >
                {resolve.isPending ? 'Resolving…' : 'Resolve handle → channel ID'}
              </Button>
              <Button type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Sync episodes from YouTube</CardTitle>
              <CardDescription>
                Pull recent uploads from this channel and create episodes for any new videos. Safe
                to re-run — already-imported videos are skipped.
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={
                  sync.isPending ||
                  (!programme.data?.youtubeChannelId && !programme.data?.youtubeHandle)
                }
                onClick={async () => {
                  try {
                    const r = await sync.mutateAsync({ programmeId });
                    showToast({
                      title: `Sync complete — ${r.created} new`,
                      description:
                        `${r.shortsCreated} short(s), ${r.skipped} skipped, ${r.scanned} scanned · ` +
                        `+${r.playlistsCreated} playlist(s)`,
                      variant: 'success',
                    });
                  } catch (err) {
                    showToast({
                      title: 'Sync failed',
                      description: (err as Error).message,
                      variant: 'danger',
                    });
                  }
                }}
              >
                <RefreshCcw className="h-3.5 w-3.5" />
                {sync.isPending ? 'Syncing…' : 'Sync now'}
              </Button>
              <Button
                type="button"
                variant="ghost"
                title="Re-scan up to 50 latest videos, ignoring the last-synced cursor. Use this when you want to backfill shorts or older episodes that the delta sync skipped."
                disabled={
                  sync.isPending ||
                  (!programme.data?.youtubeChannelId && !programme.data?.youtubeHandle)
                }
                onClick={async () => {
                  try {
                    const r = await sync.mutateAsync({
                      programmeId,
                      full: true,
                      max: 50,
                    });
                    showToast({
                      title: `Full re-scan — ${r.created} new`,
                      description:
                        `${r.shortsCreated} short(s), ${r.skipped} skipped, ${r.scanned} scanned · ` +
                        `+${r.playlistsCreated} playlist(s)`,
                      variant: 'success',
                    });
                  } catch (err) {
                    showToast({
                      title: 'Sync failed',
                      description: (err as Error).message,
                      variant: 'danger',
                    });
                  }
                }}
              >
                Full re-scan
              </Button>
            </div>
          </div>
        </CardHeader>
        {sync.data ? (
          <CardContent>
            <div className="grid grid-cols-4 gap-3 text-sm">
              <Stat label="Created" value={sync.data.created} />
              <Stat label="Of which shorts" value={sync.data.shortsCreated} />
              <Stat label="Skipped (already imported)" value={sync.data.skipped} />
              <Stat label="Scanned" value={sync.data.scanned} />
            </div>
            <div className="mt-3 grid grid-cols-3 gap-3 text-sm">
              <Stat label="Playlists discovered" value={sync.data.playlistsCreated} />
              <Stat label="Playlists refreshed" value={sync.data.playlistsRefreshed} />
              <Stat label="Playlists scanned" value={sync.data.playlistsScanned} />
            </div>
            {sync.data.cursor ? (
              <p className="mt-3 text-xs text-ink-subtle">
                Delta mode — only fetched videos newer than {fmtDateTime(sync.data.cursor)}. Use{' '}
                <strong>Full re-scan</strong> to backfill everything.
              </p>
            ) : null}
            {sync.data.shortsFetchError ? (
              <p className="mt-2 text-xs text-danger">
                Auto-shorts playlist fetch failed: <code>{sync.data.shortsFetchError}</code>.
                Duration-based classification still applies — any short-form videos in the
                uploads playlist are tagged correctly.
              </p>
            ) : null}
            {sync.data.playlistsFetchError ? (
              <p className="mt-2 text-xs text-danger">
                Channel playlists fetch failed:{' '}
                <code>{sync.data.playlistsFetchError}</code>. Episodes still synced; you
                can paste playlist IDs manually on the Playlists page.
              </p>
            ) : null}
          </CardContent>
        ) : null}
      </Card>

      {live.data?.state === 'live' && live.data.videoId ? (
        <Card>
          <CardHeader>
            <CardTitle>Now live</CardTitle>
            <CardDescription>{live.data.title ?? 'Watch the current broadcast'}</CardDescription>
          </CardHeader>
          <CardContent>
            <YouTubeEmbed videoId={live.data.videoId} title={live.data.title ?? 'Live'} />
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }): JSX.Element {
  return (
    <div className="rounded-md border border-line bg-surface px-3 py-2">
      <p className="text-xs text-ink-subtle">{label}</p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
    </div>
  );
}
